import React, { useRef, useState, useEffect, useCallback, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';

import cls from 'classnames';
import _noop from 'lodash/noop';

import { useLayoutContext } from '@ott/layout-context';
import useCloseOnOutsideEvents from './hooks/useCloseOnOutsideEvents';
import { DEFAULT, SMALL, KEYS, KEY_VALUES } from './consts';

import DropdownTrigger from './DropdownTrigger';
import DropdownOverlay from './DropdownOverlay';

import styles from './DropdownNext.scss';

export const createDropdown = ({ TriggerComponent, OverlayComponent }) =>
  React.memo(
    ({
      overlayProps,
      triggerProps,
      name,
      value,
      initial,
      size,
      tabIndex,
      className,
      valid,
      touched,
      dirty,
      mode,
      onBlur,
      onOpen,
      onClose,
      onChange,
      closeOnOutsideClick,
      closeOnResize,
      shouldCloseOverlayOnHeightChange,
      specificLocators,
    }) => {
      const { viewportWidth, viewportHeight } = useLayoutContext();

      const isMultiple = mode === 'multiple';

      const dropDownRef = useRef(null);
      const overlayRef = useRef(null);

      const shouldOpenOnFocus = useRef(true);
      const shouldCallOnClose = useRef(false); // чтоб не дублировался onClose (isOpen не успевает меняться).

      const [actualValue, setActualValue] = useState(initial || (isMultiple ? [] : {}));
      const [isOpen, setOpenState] = useState(false);
      const [isFocused, setFocusState] = useState(false);
      const [viewportSize, setViewportSize] = useState(() => ({ viewportWidth, viewportHeight }));

      const closeOverlay = useCallback(
        (params) => {
          if (shouldCallOnClose.current && onClose) {
            onClose(params);
          }
          setOpenState(false);
          setFocusState(false);
          shouldOpenOnFocus.current = true;
          shouldCallOnClose.current = false;
        },
        [onClose, shouldOpenOnFocus, shouldCallOnClose]
      );

      const handleBlur = (event) => {
        const { current: domNode } = dropDownRef;
        let { relatedTarget } = event;

        // ie 11 hack
        if (relatedTarget === null) {
          relatedTarget = document.activeElement;
        }

        if (
          !closeOnOutsideClick ||
          (overlayRef.current && overlayRef.current.contains(relatedTarget))
        ) {
          return;
        }

        if (domNode && !domNode.contains(relatedTarget)) {
          // без setTimeout не успевает проходить onClick на <Option />
          setTimeout(() => {
            closeOverlay({ revertChanges: true, checkFullName: true });
          });

          if (onBlur) {
            onBlur(event);
          }
        }
      };

      const handleTriggerPreClick = useCallback(() => {
        shouldOpenOnFocus.current = false;
      }, [shouldOpenOnFocus]);

      const handleTriggerFocus = useCallback(
        (event) => {
          setFocusState(true);

          if (shouldOpenOnFocus.current) {
            setOpenState(true);
          }

          // todo: this should be in props, not triggerProps, because in used in main component
          if (triggerProps.onFocus) {
            triggerProps.onFocus(event);
          }
        },
        [shouldOpenOnFocus, triggerProps.onFocus]
      );

      const handleTriggerClick = useCallback(() => {
        if (isOpen) {
          setOpenState(false);
          shouldCallOnClose.current = false;
          if (onClose) {
            onClose({ selectActive: true });
          }
        } else {
          setOpenState(true);
        }
      }, [isOpen, onClose, shouldCallOnClose]);

      // нужно в дополнении к handleBlur
      // т к handleBlur не всегда вызывается в safari
      useCloseOnOutsideEvents({
        ref: dropDownRef,
        callback: useCallback(() => {
          if (closeOnOutsideClick) {
            closeOverlay({ revertChanges: true, checkFullName: true });
          }
        }, [closeOnOutsideClick, closeOverlay, overlayRef]),
      });

      const onKeyDown = useCallback(
        (event) => {
          const key = event.key || String(event.keyCode);

          switch (KEY_VALUES[key]) {
            case KEYS.ENTER:
              event.preventDefault();
              closeOverlay({ selectActive: true, selectNext: true, selectByEnter: true });
              break;
            case KEYS.TAB:
              closeOverlay({ selectActive: true, selectNext: true });
              break;
            case KEYS.ESCAPE:
              closeOverlay({ revertChanges: true });
              break;
            default:
              break;
          }
        },
        [closeOverlay]
      );

      useEffect(() => {
        if (isOpen) {
          shouldCallOnClose.current = true;
          if (onOpen) {
            onOpen();
          }
        }
      }, [isOpen, onOpen, shouldCallOnClose]);

      useLayoutEffect(() => setActualValue(value), [value]);

      useEffect(() => {
        const viewportSizeChanged =
          viewportWidth !== viewportSize.viewportWidth ||
          (shouldCloseOverlayOnHeightChange && viewportHeight !== viewportSize.viewportHeight);

        if (viewportSizeChanged) {
          setViewportSize({ viewportWidth, viewportHeight });
        }

        if (closeOnResize && isOpen && viewportSizeChanged) {
          closeOverlay({ revertChanges: true });
        }
      }, [viewportWidth, viewportHeight, isOpen, closeOnResize, dropDownRef]);

      const isNewError = !valid && (touched || dirty);

      let hasValue = !!actualValue;

      if (isMultiple && actualValue) {
        hasValue = actualValue.length > 0;
      }

      return (
        <div
          ref={dropDownRef}
          className={cls(styles.Dropdown, className)}
          onKeyDown={isMultiple ? _noop : onKeyDown}
          onBlur={handleBlur}
          tabIndex={-1}
          data-locator={cls('Dropdown', specificLocators)}
        >
          <TriggerComponent
            name={name}
            value={actualValue}
            isOpen={isOpen}
            isFocused={isFocused}
            size={size}
            isError={isNewError}
            tabIndex={tabIndex}
            {...triggerProps}
            onMouseDown={handleTriggerPreClick}
            onTouchEnd={handleTriggerPreClick}
            onFocus={handleTriggerFocus}
            onClick={handleTriggerClick}
            closeOverlay={closeOverlay}
            setOpenState={setOpenState}
            isMultiple={isMultiple}
            hasValue={hasValue}
          />
          {isOpen && (
            <OverlayComponent
              overlayRef={overlayRef}
              onBlur={handleBlur}
              container={dropDownRef.current}
              value={actualValue}
              size={size}
              closeOverlay={closeOverlay}
              tabIndex={-1}
              onChange={onChange}
              isMultiple={isMultiple}
              {...overlayProps}
            />
          )}
        </div>
      );
    }
  );

const Dropdown = createDropdown({
  TriggerComponent: DropdownTrigger,
  OverlayComponent: DropdownOverlay,
});

Dropdown.displayName = 'DropdownNext';

Dropdown.defaultProps = {
  size: DEFAULT,
  tabIndex: 0,
  className: '',
  triggerProps: {},
  closeOnOutsideClick: true,
  closeOnResize: true,
  mode: 'single',
  shouldCloseOverlayOnHeightChange: true,
};

Dropdown.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  overlayProps: PropTypes.object, // blackbox forwarded to Overlay component
  // eslint-disable-next-line react/forbid-prop-types
  triggerProps: PropTypes.object.isRequired, // blackbox forwarded to Trigger component
  mode: PropTypes.oneOf(['single', 'multiple']),
  name: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  size: PropTypes.oneOf([DEFAULT, SMALL]),
  tabIndex: PropTypes.number,
  className: PropTypes.string,
  onBlur: PropTypes.func,
  closeOnOutsideClick: PropTypes.bool,
  closeOnResize: PropTypes.bool,
  shouldCloseOverlayOnHeightChange: PropTypes.bool,
  specificLocators: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
};

export default Dropdown;
